import { useEffect, useRef, useState } from "react";
import { http, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  UploadSimple, X, Star, Trash, PencilSimple, Check, MapPin, Sparkle,
} from "@phosphor-icons/react";

// PhotosCmsTab — admin panel for uploading & curating the NaturePulse photo
// library. Photos are auto-placed on the site via <CmsPhotoRail /> based on
// their category + tags.

const CATEGORIES = [
  "sunsets", "plants", "flowers", "animals", "birds", "insects",
  "trees", "landscape", "rocks", "hills", "mountains",
  "rain", "snow", "hail", "clouds",
  "cityscape", "streets", "roads",
  "beach", "lake", "rivers", "wetlands",
  "agriculture", "wildlife", "forests", "other",
];

function absUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API_BASE}${url}`;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

// -------- Upload flow (multi-file, previews, per-file category + tags) --------
function UploadPanel({ onDone }) {
  const [queue, setQueue] = useState([]); // [{file, url, category, tags, caption}]
  const [busy, setBusy] = useState(false);
  const [defaultCat, setDefaultCat] = useState("plants");
  const inputRef = useRef(null);

  const pickFiles = () => inputRef.current?.click();

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const additions = files.slice(0, 20).map((f) => ({
      file: f,
      url: URL.createObjectURL(f),
      category: defaultCat,
      tags: "",
      caption: "",
    }));
    setQueue((q) => [...q, ...additions]);
    e.target.value = "";
  };

  const updateItem = (i, patch) =>
    setQueue((q) => q.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeItem = (i) =>
    setQueue((q) => q.filter((_, idx) => idx !== i));

  const uploadAll = async () => {
    if (queue.length === 0) { toast.info("Pick a photo first."); return; }
    setBusy(true);
    let ok = 0, fail = 0;
    for (const item of queue) {
      try {
        const b64 = await fileToBase64(item.file);
        await http.post("/cms/photos", {
          image_base64: b64,
          content_type: item.file.type || "image/jpeg",
          category: item.category,
          tags: item.tags.split(",").map((t) => t.trim()).filter(Boolean),
          caption: item.caption || null,
        });
        ok += 1;
      } catch (e) {
        console.warn("upload failed", e);
        fail += 1;
      }
    }
    setBusy(false);
    setQueue([]);
    toast.success(`${ok} uploaded${fail ? ` · ${fail} failed` : ""}`);
    onDone?.();
  };

  return (
    <div className="rounded-xl bg-white border border-black/15 p-5" data-testid="cms-upload-panel">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="min-w-0">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#E4A15C]">/ Upload</div>
          <div className="font-serif-display text-2xl text-[#151312] mt-1">Add nature photos</div>
          <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#3A3830] mt-1">
            JPG / PNG / WEBP up to 12 MB · pick many at once · phone camera supported
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={defaultCat}
            onChange={(e) => setDefaultCat(e.target.value)}
            data-testid="cms-upload-default-cat"
            className="bg-transparent border border-black/15 rounded-full text-[#151312] font-mono-tech text-[10px] tracking-[0.22em] uppercase px-3 py-2"
          >
            {CATEGORIES.map((c) => <option key={c} value={c} className="text-[#151312]">{c}</option>)}
          </select>
          <button
            onClick={pickFiles}
            data-testid="cms-upload-pick"
            className="inline-flex items-center gap-2 rounded-full bg-[#E4A15C] text-[#151312] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:brightness-110"
          >
            <UploadSimple size={14} weight="bold" /> Pick photos
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={onFiles}
          className="hidden"
          data-testid="cms-upload-input"
        />
      </div>

      {queue.length > 0 && (
        <div className="mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {queue.map((it, i) => (
              <div key={i} className="flex gap-3 rounded-lg bg-black/5 border border-black/15 p-3" data-testid={`cms-queue-item-${i}`}>
                <img src={it.url} alt="" className="w-20 h-20 rounded object-cover shrink-0" />
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <select
                      value={it.category}
                      onChange={(e) => updateItem(i, { category: e.target.value })}
                      data-testid={`cms-queue-cat-${i}`}
                      className="bg-transparent border border-black/15 rounded text-[#151312] font-mono-tech text-[10px] tracking-[0.22em] uppercase px-2 py-1"
                    >
                      {CATEGORIES.map((c) => <option key={c} value={c} className="text-[#151312]">{c}</option>)}
                    </select>
                    <button
                      onClick={() => removeItem(i)}
                      data-testid={`cms-queue-remove-${i}`}
                      className="ml-auto text-[#3A3830] hover:text-[#151312] p-1"
                      aria-label="Remove"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                  <input
                    value={it.tags}
                    onChange={(e) => updateItem(i, { tags: e.target.value })}
                    placeholder="tags, comma, separated"
                    data-testid={`cms-queue-tags-${i}`}
                    className="w-full bg-transparent border border-black/15 rounded px-2 py-1 text-xs text-[#151312] placeholder:text-[#3A3830]"
                  />
                  <input
                    value={it.caption}
                    onChange={(e) => updateItem(i, { caption: e.target.value })}
                    placeholder="optional caption — where, when, what"
                    data-testid={`cms-queue-caption-${i}`}
                    className="w-full bg-transparent border border-black/15 rounded px-2 py-1 text-xs text-[#151312] placeholder:text-[#3A3830]"
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button
              disabled={busy}
              onClick={uploadAll}
              data-testid="cms-upload-submit"
              className="inline-flex items-center gap-2 rounded-full bg-[#F5EBDC] text-[#151312] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50"
            >
              {busy ? "Uploading…" : `Upload ${queue.length} photo${queue.length === 1 ? "" : "s"}`}
            </button>
            <button
              onClick={() => setQueue([])}
              disabled={busy}
              className="text-[#3A3830] hover:text-[#151312] font-mono-tech text-[10px] tracking-[0.28em] uppercase"
            >Clear</button>
          </div>
        </div>
      )}
    </div>
  );
}

// -------- Photo card in the grid --------
function PhotoCard({ photo, onDelete, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [tags, setTags] = useState((photo.tags || []).join(", "));
  const [caption, setCaption] = useState(photo.caption || "");
  const [category, setCategory] = useState(photo.category);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await http.patch(`/cms/photos/${photo.id}`, {
        category,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        caption: caption || null,
      });
      onUpdate?.(data);
      setEditing(false);
      toast.success("Saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const toggleFeatured = async () => {
    try {
      const { data } = await http.patch(`/cms/photos/${photo.id}`, { featured: !photo.featured });
      onUpdate?.(data);
    } catch { toast.error("Couldn't update"); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await http.delete(`/cms/photos/${photo.id}`);
      onDelete?.(photo.id);
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="rounded-xl overflow-hidden border border-black/15 bg-black/5 flex flex-col" data-testid={`cms-card-${photo.id}`}>
      <div className="relative">
        <img src={absUrl(photo.url)} alt={photo.caption || photo.category} className="w-full h-44 object-cover" />
        <button
          onClick={toggleFeatured}
          data-testid={`cms-star-${photo.id}`}
          className={`absolute top-2 left-2 p-1.5 rounded-full ${photo.featured ? "bg-[#E4A15C] text-[#151312]" : "bg-black/50 text-[#F5EBDC]"}`}
          aria-label="Toggle featured"
        >
          <Star size={13} weight={photo.featured ? "fill" : "regular"} />
        </button>
      </div>
      {!editing ? (
        <div className="p-3 flex-1 flex flex-col gap-1.5 text-[#151312]">
          <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#E4A15C]">{photo.category}</div>
          {photo.caption && <div className="text-xs italic text-[#3A3830] line-clamp-2">{photo.caption}</div>}
          {photo.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {photo.tags.slice(0, 6).map((t) => (
                <span key={t} className="rounded-full px-2 py-0.5 bg-black/5 text-[9px] font-mono-tech tracking-[0.15em]">{t}</span>
              ))}
            </div>
          )}
          <div className="mt-auto pt-2 flex items-center gap-2">
            <button
              onClick={() => setEditing(true)}
              data-testid={`cms-edit-${photo.id}`}
              className="inline-flex items-center gap-1 rounded-full border border-black/15 px-3 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-black/5"
            >
              <PencilSimple size={11} /> Edit
            </button>
            <button
              onClick={remove}
              data-testid={`cms-delete-${photo.id}`}
              className="inline-flex items-center gap-1 rounded-full border border-red-400/40 text-red-300 px-3 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-red-500/10"
            >
              <Trash size={11} /> Delete
            </button>
          </div>
        </div>
      ) : (
        <div className="p-3 space-y-2 text-[#151312]">
          <select
            value={category} onChange={(e) => setCategory(e.target.value)}
            className="w-full bg-transparent border border-black/15 rounded font-mono-tech text-[10px] tracking-[0.2em] uppercase px-2 py-1"
          >
            {CATEGORIES.map((c) => <option key={c} value={c} className="text-[#151312]">{c}</option>)}
          </select>
          <input
            value={tags} onChange={(e) => setTags(e.target.value)}
            placeholder="tags, comma, separated"
            className="w-full bg-transparent border border-black/15 rounded px-2 py-1 text-xs"
          />
          <textarea
            value={caption} onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption"
            rows={2}
            className="w-full bg-transparent border border-black/15 rounded px-2 py-1 text-xs"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={save} disabled={saving}
              data-testid={`cms-save-${photo.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-[#E4A15C] text-[#151312] px-3 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase disabled:opacity-50"
            >
              <Check size={11} weight="bold" /> Save
            </button>
            <button
              onClick={() => setEditing(false)}
              className="text-[#3A3830] hover:text-[#151312] font-mono-tech text-[9px] tracking-[0.22em] uppercase"
            >Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// -------- Main tab --------
export default function PhotosCmsTab() {
  const [photos, setPhotos] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const loadCats = () =>
    http.get("/cms/categories").then((r) => setCats(r.data?.categories || [])).catch(() => {});
  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(); params.set("limit", "200");
      if (filter) params.set("category", filter);
      const { data } = await http.get(`/cms/photos?${params.toString()}`);
      setPhotos(data.photos || []);
    } catch (e) { toast.error("Failed to load photos"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadCats(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const onUploaded = () => { load(); loadCats(); };
  const onDelete = (id) => { setPhotos((p) => p.filter((x) => x.id !== id)); loadCats(); };
  const onUpdate = (updated) => { setPhotos((p) => p.map((x) => x.id === updated.id ? updated : x)); loadCats(); };

  return (
    <div className="space-y-6" data-testid="cms-photos-tab">
      <UploadPanel onDone={onUploaded} />

      {/* Filter chips */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setFilter("")}
          data-testid="cms-filter-all"
          className={`rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase border ${
            !filter ? "bg-[#E4A15C] text-[#151312] border-[#E4A15C]" : "border-black/15 text-[#3A3830] hover:bg-black/5"
          }`}
        >
          All · {cats.reduce((s, c) => s + c.count, 0)}
        </button>
        {cats.filter((c) => c.count > 0).map((c) => (
          <button
            key={c.key}
            onClick={() => setFilter(c.key)}
            data-testid={`cms-filter-${c.key}`}
            className={`rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase border ${
              filter === c.key ? "bg-[#E4A15C] text-[#151312] border-[#E4A15C]" : "border-black/15 text-[#3A3830] hover:bg-black/5"
            }`}
          >
            {c.key} · {c.count}
          </button>
        ))}
      </div>

      {loading && <div className="text-[#3A3830] font-mono-tech text-xs tracking-widest">Loading…</div>}

      {photos.length === 0 && !loading ? (
        <div className="rounded-xl border border-dashed border-black/15 p-8 text-center text-[#3A3830]">
          <Sparkle size={24} weight="fill" className="mx-auto text-[#E4A15C] mb-2" />
          <div className="font-serif-display text-xl text-[#151312]">Your photo library is empty.</div>
          <div className="mt-1 text-xs">Tap “Pick photos” above to upload the first one.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {photos.map((p) => (
            <PhotoCard key={p.id} photo={p} onDelete={onDelete} onUpdate={onUpdate} />
          ))}
        </div>
      )}
    </div>
  );
}
